import { Record } from "./fable_modules/fable-library.4.10.0/Types.js";
import { class_type, record_type, bool_type, string_type, obj_type } from "./fable_modules/fable-library.4.10.0/Reflection.js";
import { map as map_1, toArray, ofArray } from "./fable_modules/fable-library.4.10.0/List.js";
import { keyValueList } from "./fable_modules/fable-library.4.10.0/MapUtil.js";
import { disposeSafe, getEnumerator } from "./fable_modules/fable-library.4.10.0/Util.js";
import * as react from "react";
import react_flatpickr from "react-flatpickr";

class OptionType extends Record {
    constructor(Value, Key, IsConfig) {
        super();
        this.Value = Value;
        this.Key = Key;
        this.IsConfig = IsConfig;
    }
}

function OptionType_$reflection() {
    return record_type("Flatpickr.OptionType", [], OptionType, () => [["Value", obj_type], ["Key", string_type], ["IsConfig", bool_type]]);
}

/**
 * Sets the initial value for the Flatpickr component
 */
export function Value(date) {
    return new OptionType(date, "value", false);
}

/**
 * Determines whether or not the date picker also contains a time picker
 */
export function EnableTimePicker(cond) {
    return new OptionType(cond, "enableTime", true);
}

/**
 * Enables seconds in the time picker
 */
export function EnableSecondsPicker(cond) {
    return new OptionType(cond, "enableSeconds", true);
}

/**
 * The minimum date that a user can start picking from (inclusive).
 */
export function MinimumDate(date) {
    return new OptionType(date, "minDate", true);
}

/**
 * The minimum date that a user can start picking from (inclusive).
 */
export function MaximumDate(date) {
    return new OptionType(date, "maxDate", true);
}

/**
 * A placeholder text for the input. It will be shown when no date value is selected.
 */
export function Placeholder(placeholder) {
    return new OptionType(placeholder, "placeholder", false);
}

/**
 * Defines the id of the input
 */
export function Id(id) {
    return new OptionType(id, "id", false);
}

/**
 * Defines a custom property with key and value and flag that tells whether the option is from the flatpickr library or just an attribute of the input element
 */
export function custom(key, value, config) {
    return new OptionType(value, key, config);
}

/**
 * Sets the initial value of the hour element (12 by default)
 */
export function DefaultHour(hour) {
    return new OptionType(hour, "defaultHour", true);
}

/**
 * Sets the initial value of the minute element (0 by default)
 */
export function DefaultMinute(min) {
    return new OptionType(min, "defaultMinute", true);
}

/**
 * Enables display of week numbers in calendar.
 */
export function EnableWeekNumbers(cond) {
    return new OptionType(cond, "weekNumbers", true);
}

/**
 * Registers an event handler for Flatpickr that is triggered when the user selects a new datetime value
 */
export function OnChange(callback) {
    return new OptionType((dates) => {
        if ((dates == null) ? true : (dates.length === 0)) {
        }
        else {
            callback(dates[0]);
        }
    }, "onChange", false);
}

/**
 * Registers an event handler for Flatpickr that is triggered when the user selects a new datetime value
 */
export function OnManyChanged(callback) {
    return new OptionType((dates) => {
        callback(ofArray(dates));
    }, "onChange", false);
}

/**
 * Defines the class attribute for the Flatpickr element. Keep in mind that Flatpickr is implmented as wrapper around an `<input />` element.
 */
export function ClassName(name) {
    return new OptionType(name, "className", false);
}

/**
 * Defines the inline styles of the Flatpickr element
 */
export function Style(props) {
    return new OptionType(keyValueList(props, 1), "style", false);
}

/**
 * Defines whether or not the Flatpickr element is disabled.
 */
export function Disabled(value) {
    return new OptionType(value, "disabled", false);
}

/**
 * Defines how the date is displayed on the component
 */
export function DateFormat(value) {
    return new OptionType(value, "dateFormat", true);
}

/**
 * Hides the calendar so that users cannot select dates
 */
export function HideCalendar(value) {
    return new OptionType(value, "noCalendar", true);
}

/**
 * The selection mode changes wether the use can select a single value, multiple values or an inclusive range of dates.
 */
export function SelectionMode(mode) {
    return new OptionType(mode, "mode", true);
}

/**
 * If set to true, makes sure that the picker is always set shown to the user
 */
export function AlwaysOpen(value) {
    return new OptionType(value, "inline", true);
}

/**
 * Disallow the user to select the given dates
 */
export function DisableDates(dates) {
    return new OptionType(toArray(dates), "disable", true);
}

/**
 * Disallow the user to select the dates using a predicate
 */
export function DisableBy(pred) {
    return new OptionType([pred], "disable", true);
}

/**
 * Adjusts the step for the minute input (incl. scrolling)
 */
export function MinuteIncrement(min) {
    return new OptionType(min, "minuteIncrement", true);
}

/**
 * Adjusts the step for the hour input (incl. scrolling)
 */
export function HourIncrement(hours) {
    return new OptionType(hours, "hourIncrement", true);
}

/**
 * Disallow the user to select the dates that are between the given ranges
 */
export function DisableRanges(ranges) {
    return new OptionType(toArray(map_1((tupledArg) => {
        const range = {};
        range["to"] = tupledArg[1];
        range["from"] = tupledArg[0];
        return range;
    }, ranges)), "disable", true);
}

/**
 * Enable only the given list of dates
 */
export function EnableDates(dates) {
    return new OptionType(toArray(dates), "enable", true);
}

/**
 * Enable only the dates that pass a given criteria
 */
export function EnableBy(pred) {
    return new OptionType([pred], "enable", true);
}

/**
 * Allows using a custom date formatting function instead of the built-in handling for date formats using dateFormat.
 */
export function FormatDateBy(map) {
    return new OptionType(map, "formatDate", true);
}

/**
 * Displays time picker in 24 hour mode without AM/PM selection when enabled.
 */
export function TimeTwentyFour(cond) {
    return new OptionType(cond, "time_24hr", true);
}

/**
 * Show the month using the shorthand version (ie, Sep instead of September).
 */
export function UseShorthandMonthNames(cond) {
    return new OptionType(cond, "shorthandCurrentMonth", true);
}

/**
 * Enable only the given list of date ranges
 */
export function EnableRanges(ranges) {
    return new OptionType(toArray(map_1((tupledArg) => {
        const range = {};
        range["to"] = tupledArg[1];
        range["from"] = tupledArg[0];
        return range;
    }, ranges)), "enable", true);
}

/**
 * Localize the instance
 */
export function Locale(loc) {
    return new OptionType(loc, "locale", true);
}

/**
 * Initializes Flatpickr, a lightweight DateTime picker.
 */
export function flatpickr(options) {
    const props = {};
    const propOptions = {};
    const enumerator = getEnumerator(options);
    try {
        while (enumerator["System.Collections.IEnumerator.MoveNext"]()) {
            const option = enumerator["System.Collections.Generic.IEnumerator`1.get_Current"]();
            if (option.IsConfig) {
                propOptions[option.Key] = option.Value;
            }
            else {
                props[option.Key] = option.Value;
            }
        }
    }
    finally {
        disposeSafe(enumerator);
    }
    props["options"] = propOptions;
    return react.createElement(react_flatpickr, props);
}

export class Locales {
    constructor() {
    }
}

export function Locales_$reflection() {
    return class_type("Flatpickr.Locales", void 0, Locales);
}

